/* screens/crm/CrmShell.jsx — раздел CRM: переключатель воронок (вкладки шапки) +
   канбан-доска + дровера лида/формы/конструктора + модалка причины перемещения.
   Глобальный раздел (не привязан к пространству); доступ — гейт RequireCrmAccess. */
import React from 'react';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { EmptyState, ErrorState } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { SearchInput } from '../../components/ui/Field.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { RR } from '../../lib/router.jsx';
import * as crm from '../../lib/api/crm.js';
import { Board } from './Board.jsx';
import { LeadDrawer } from './LeadDrawer.jsx';
import { LeadFormDrawer } from './LeadFormDrawer.jsx';
import { LeadArchiveDrawer } from './LeadArchiveDrawer.jsx';
import { MoveReasonModal } from './MoveReasonModal.jsx';

const { useState, useMemo, useCallback, useEffect } = React;
const { useSearchParams, useNavigate } = RR;
const { useFakeQuery, useBreakpoint } = hooks;

function CrmScreen() {
  const toast = useToast();
  const confirm = useConfirm();
  const { user } = useAuth();
  const isAdmin = !!user?.crm_admin; // настройка воронок — только админ (crm.manage_crm)
  const bp = useBreakpoint();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const metaQ = useFakeQuery('crm-meta', () => crm.getCrmMeta(), []);
  const meta = metaQ.data || null;
  const staffQ = useFakeQuery('crm-staff', () => crm.listStaff(), []);
  const staff = staffQ.data || [];

  // Фильтры доски (ответственный / источник / поиск) + архив.
  const [filters, setFilters] = useState({ q: '', source: '', owner: '' });
  const [archiveOpen, setArchiveOpen] = useState(false);
  const setFilter = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  const [pipelinesNonce, setPipelinesNonce] = useState(0);
  const pipelinesQ = useFakeQuery('crm-pipelines-' + pipelinesNonce, () => crm.listPipelines(), [pipelinesNonce]);
  const pipelines = pipelinesQ.data || [];

  const defaultPipeline = useMemo(
    () => pipelines.find((p) => p.is_default) || pipelines[0] || null, [pipelines]);
  const pipelineParam = params.get('pipeline');
  const activeId = useMemo(() => {
    const pid = pipelineParam ? Number(pipelineParam) : null;
    if (pid && pipelines.some((p) => p.id === pid)) return pid;
    return defaultPipeline ? defaultPipeline.id : null;
  }, [pipelineParam, pipelines, defaultPipeline]);
  const activePipeline = useMemo(() => pipelines.find((p) => p.id === activeId) || null, [pipelines, activeId]);
  const stageById = useMemo(() => {
    const m = {};
    (activePipeline?.stages || []).forEach((s) => { m[s.id] = s; });
    return m;
  }, [activePipeline]);

  const [boardNonce, setBoardNonce] = useState(0);
  const boardQ = useFakeQuery(
    `crm-board-${activeId}-${boardNonce}-${filters.q}-${filters.source}-${filters.owner}`,
    () => (activeId ? crm.getBoard(activeId, filters) : Promise.resolve(null)),
    [activeId, boardNonce, filters]);

  const [leadNonce, setLeadNonce] = useState(0);
  const reloadBoard = useCallback(() => setBoardNonce((n) => n + 1), []);
  const refreshAll = useCallback(() => {
    setBoardNonce((n) => n + 1);
    setPipelinesNonce((n) => n + 1);
  }, []);

  // — URL-helpers (deep-link воронки и лида) —
  const setParam = useCallback((key, value) => {
    setParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (value == null || value === '') sp.delete(key); else sp.set(key, String(value));
      return sp;
    });
  }, [setParams]);

  const leadParam = params.get('lead');
  const selectedLeadId = leadParam ? Number(leadParam) : null;
  // Стабильные ссылки (useCallback) — чтобы дровер лида не пересоздавал onClose
  // и не ловил устаревшие замыкания.
  const openLead = useCallback((lead) => setParam('lead', lead.id), [setParam]);
  const closeLead = useCallback(() => setParam('lead', null), [setParam]);
  const switchPipeline = useCallback((id) => setParams((prev) => {
    const sp = new URLSearchParams(prev);
    sp.set('pipeline', String(id));
    sp.delete('lead'); // лид из другой воронки закрываем
    return sp;
  }), [setParams]);

  // — Создание лида —
  const [leadForm, setLeadForm] = useState(null); // {presetStageId?} | null
  // — Перемещение (модалка причины) —
  const [pendingMove, setPendingMove] = useState(null); // {lead, fromStage, presetToStage} | null

  const requestMoveFromBoard = useCallback((lead, toStage) =>
    setPendingMove({ lead, fromStage: stageById[lead.stage] || null, presetToStage: toStage }), [stageById]);
  const requestMoveFromDrawer = useCallback((lead) =>
    setPendingMove({ lead, fromStage: stageById[lead.stage] || null, presetToStage: null }), [stageById]);

  const confirmMove = async (toStageId, reason) => {
    const { lead } = pendingMove;
    await crm.moveLead(lead.id, toStageId, reason); // бросит → модалка покажет ошибку
    setPendingMove(null);
    reloadBoard();
    setPipelinesNonce((n) => n + 1);
    setLeadNonce((n) => n + 1); // открытый дровер лида перечитает этап/историю
    toast.show('Лид перемещён', 'accent');
  };

  // — Массовый выбор и действия над пачкой лидов —
  const [selected, setSelected] = useState(() => new Set());
  const [batchMoveOpen, setBatchMoveOpen] = useState(false);
  const toggleSelect = useCallback((id) => setSelected((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  }), []);
  const clearSelection = useCallback(() => setSelected(new Set()), []);
  useEffect(() => { setSelected(new Set()); }, [activeId]); // смена воронки сбрасывает выбор

  const batchArchive = async () => {
    const ok = await confirm({ title: `Архивировать лидов: ${selected.size}?`,
      body: 'Они скроются с доски — вернуть можно из архива.', confirmLabel: 'В архив', tone: 'danger' });
    if (!ok) return;
    try {
      const r = await crm.batchUpdateLeads({ ids: [...selected], action: 'archive' });
      toast.show(`В архиве: ${r.updated}`, 'accent'); clearSelection(); refreshAll();
    } catch (e) { toast.show(e?.detail || 'Не удалось архивировать.', 'danger'); }
  };
  const batchReassign = async (v) => {
    if (v === '') return;
    const owner = v === '__unassign__' ? null : Number(v);
    try {
      const r = await crm.batchUpdateLeads({ ids: [...selected], action: 'reassign', owner });
      toast.show(`Назначено: ${r.updated}`, 'accent'); clearSelection(); refreshAll();
    } catch (e) { toast.show(e?.detail || 'Не удалось назначить.', 'danger'); }
  };
  // Перенос лида в другую воронку: move → доску переключаем на целевую воронку и
  // переоткрываем лид там; copy → исходный остаётся, просто обновляем данные.
  const onLeadTransferred = useCallback((result, payload) => {
    if (payload.copy) { refreshAll(); return; }
    setParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.set('pipeline', String(payload.to_pipeline));
      sp.set('lead', String(result.id));
      return sp;
    });
    setBoardNonce((n) => n + 1);
    setPipelinesNonce((n) => n + 1);
    setLeadNonce((n) => n + 1);
  }, [refreshAll, setParams]);

  const confirmBatchMove = async (toStageId, reason) => {
    const r = await crm.batchUpdateLeads({ ids: [...selected], action: 'move', to_stage: toStageId, reason });
    setBatchMoveOpen(false); clearSelection(); refreshAll();
    toast.show(`Перемещено: ${r.updated}`, 'accent');
  };

  const createFirstPipeline = async () => {
    try {
      await crm.createPipeline({ name: 'Воронка продаж', is_default: true });
      refreshAll();
    } catch (e) {
      toast.show(e?.detail || 'Не удалось создать воронку.', 'danger');
    }
  };

  // — Состояния загрузки/ошибки —
  // Полноэкранный лоадер — ТОЛЬКО на первичной загрузке (нет данных). На
  // рефетчах (после заметки/перемещения) данные сохраняются → доска и дровер
  // не размонтируются (иначе терялась бы вкладка дровера и был бы flash).
  if ((pipelinesQ.loading && !pipelines.length) || (metaQ.loading && !meta)) {
    return (
      <div className="section-page">
        <PageHeader breadcrumbs={[{ label: 'Админка', to: '/console' }, { label: 'CRM' }]} title="CRM · Воронки продаж" />
        <div className="section-body"><div className="state state--loading"><Spinner size="lg" label="Загрузка…" /></div></div>
      </div>
    );
  }
  if (pipelinesQ.error) {
    return (
      <div className="section-page">
        <PageHeader breadcrumbs={[{ label: 'Админка', to: '/console' }, { label: 'CRM' }]} title="CRM · Воронки продаж" />
        <div className="section-body"><ErrorState title="Не удалось загрузить CRM" onRetry={pipelinesQ.reload} /></div>
      </div>
    );
  }

  const tabs = pipelines.map((p) => ({ id: p.id, label: p.name, count: p.lead_count }));
  const actions = (
    <>
      <Button variant="ghost" iconLeft="arrow-left" onClick={() => navigate('/console')}>Сервисы</Button>
      {activePipeline && isAdmin && (
        <Button variant="secondary" iconLeft="settings" onClick={() => navigate('/console/crm/settings/' + activePipeline.id)}>Воронка</Button>
      )}
      {activePipeline && (
        <Button variant="primary" iconLeft="plus" onClick={() => setLeadForm({})}>Новый лид</Button>
      )}
    </>
  );

  // Доску показываем только когда данные соответствуют активной воронке (иначе —
  // спиннер: при переключении воронки старые данные не мелькают).
  const boardData = (boardQ.data && boardQ.data.pipeline && boardQ.data.pipeline.id === activeId)
    ? boardQ.data : null;
  const hasStages = (activePipeline?.stages || []).length > 0;

  return (
    <div className="section-page">
      <PageHeader breadcrumbs={[{ label: 'Админка', to: '/console' }, { label: 'CRM' }]} title="CRM · Воронки продаж"
        tabs={tabs.length > 1 ? tabs : null} activeTab={activeId} onTab={switchPipeline}
        actions={actions} />

      <div className="section-body crm-board-wrap">
        {pipelines.length > 0 && hasStages && (
          <div className="crm-toolbar">
            <SearchInput value={filters.q} onChange={setFilter('q')} placeholder="Поиск лида…"
              className="crm-toolbar__search" />
            <div className="crm-toolbar__filter">
              <Select value={filters.source} onChange={setFilter('source')}
                options={[{ value: '', label: 'Все источники' }, ...(meta?.sources || [])]}
                placeholder="Все источники" searchable={(meta?.sources || []).length > 6} />
            </div>
            <div className="crm-toolbar__filter">
              <Select value={filters.owner} onChange={setFilter('owner')}
                options={[{ value: '', label: 'Все ответственные' },
                  ...staff.map((u) => ({ value: String(u.id), label: u.full_name }))]}
                placeholder="Все ответственные" searchable={staff.length > 6} />
            </div>
            <div className="grow" />
            <Button variant="ghost" iconLeft="box" onClick={() => setArchiveOpen(true)}>Архив</Button>
          </div>
        )}
        {selected.size > 0 && (
          <div className="crm-selbar">
            <span className="crm-selbar__count">Выбрано: {selected.size}</span>
            <Button size="sm" variant="ghost" iconLeft="arrow-right" onClick={() => setBatchMoveOpen(true)}>Переместить</Button>
            <Button size="sm" variant="ghost" iconLeft="trash" onClick={batchArchive}>В архив</Button>
            <div className="crm-selbar__assign">
              <Select value="" onChange={batchReassign}
                options={[{ value: '__unassign__', label: '— Снять ответственного —' },
                  ...staff.map((u) => ({ value: String(u.id), label: u.full_name }))]}
                placeholder="Назначить…" searchable={staff.length > 6} />
            </div>
            <div className="grow" />
            <Button size="sm" variant="ghost" onClick={clearSelection}>Снять выбор</Button>
          </div>
        )}
        {pipelines.length === 0 ? (
          <EmptyState icon="layers" title="Пока нет воронок"
            body={isAdmin
              ? 'Создайте первую воронку продаж, чтобы складывать в неё лиды и вести их по этапам.'
              : 'Воронки ещё не настроены. Обратитесь к администратору CRM.'}
            action={isAdmin
              ? <Button variant="primary" iconLeft="plus" onClick={createFirstPipeline}>Создать воронку</Button>
              : null} />
        ) : !hasStages ? (
          <EmptyState icon="layers" title="В воронке нет этапов"
            body={isAdmin
              ? 'Добавьте этапы в конструкторе воронки, чтобы появилась доска.'
              : 'В воронке ещё нет этапов. Обратитесь к администратору CRM.'}
            action={isAdmin
              ? <Button variant="primary" iconLeft="settings" onClick={() => navigate('/console/crm/settings')}>Открыть конструктор</Button>
              : null} />
        ) : !boardData ? (
          boardQ.error
            ? <ErrorState title="Не удалось загрузить доску" onRetry={boardQ.reload} />
            : <div className="state state--loading"><Spinner size="lg" label="Загрузка доски…" /></div>
        ) : (
          <Board
            columns={boardData.columns}
            mobile={bp.isMobile}
            selectedIds={selected}
            onToggleSelect={toggleSelect}
            onOpenLead={openLead}
            onAddLead={(stage) => setLeadForm({ presetStageId: stage.id })}
            onRequestMove={requestMoveFromBoard}
          />
        )}
      </div>

      {selectedLeadId && (
        <LeadDrawer
          leadId={selectedLeadId}
          sources={meta?.sources || []}
          staff={staff}
          pipelines={pipelines}
          presets={meta?.move_reason_presets || []}
          currentUser={user}
          reloadNonce={leadNonce}
          onClose={closeLead}
          onRequestMove={requestMoveFromDrawer}
          onChanged={refreshAll}
          onTransferred={onLeadTransferred}
        />
      )}

      {leadForm && activePipeline && (
        <LeadFormDrawer
          pipelineId={activePipeline.id}
          presetStageId={leadForm.presetStageId}
          sources={meta?.sources || []}
          staff={staff}
          customFields={activePipeline.custom_fields || []}
          currentUser={user}
          onClose={() => setLeadForm(null)}
          onCreated={(lead) => { setLeadForm(null); refreshAll(); openLead(lead); }}
        />
      )}

      {pendingMove && (
        <MoveReasonModal
          open
          lead={pendingMove.lead}
          fromStage={pendingMove.fromStage}
          presetToStage={pendingMove.presetToStage}
          stages={activePipeline?.stages || []}
          presets={meta?.move_reason_presets || []}
          onCancel={() => setPendingMove(null)}
          onConfirm={confirmMove}
        />
      )}

      {archiveOpen && activePipeline && (
        <LeadArchiveDrawer
          pipelineId={activePipeline.id}
          onClose={() => setArchiveOpen(false)}
          onRestored={refreshAll}
        />
      )}

      {batchMoveOpen && activePipeline && (
        <MoveReasonModal
          open
          batchCount={selected.size}
          stages={activePipeline.stages || []}
          presets={meta?.move_reason_presets || []}
          onCancel={() => setBatchMoveOpen(false)}
          onConfirm={confirmBatchMove}
        />
      )}
    </div>
  );
}

export { CrmScreen };
